import { useState, useContext, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Badge,
  Stack,
  Modal,
} from "react-bootstrap";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { WorkService } from "../../services";
import { LanguageContext } from "../../utils/Language";
import { getStepComponent } from "./step-forms/StepRegistry";
import "./WorkReceipt.css";

// Helper functions for photo scanner preprocessing and downscaling
const loadImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(new Error("Failed to load image element: " + err.message));
      img.src = event.target.result;
    };
    reader.onerror = (err) => reject(new Error("Failed to read file: " + err.message));
    reader.readAsDataURL(file);
  });
};

const preprocessImage = (img, mimeType, options) => {
  return new Promise((resolve) => {
    const { maxDim, cropRatio, type } = options;
    const origWidth = img.width;
    const origHeight = img.height;

    // Calculate downscaled dimensions
    let targetWidth = origWidth;
    let targetHeight = origHeight;

    if (targetWidth > maxDim || targetHeight > maxDim) {
      if (targetWidth > targetHeight) {
        targetHeight = Math.round((targetHeight * maxDim) / targetWidth);
        targetWidth = maxDim;
      } else {
        targetWidth = Math.round((targetWidth * maxDim) / targetHeight);
        targetHeight = maxDim;
      }
    }

    let canvasWidth = targetWidth;
    let canvasHeight = targetHeight;
    let sx = 0, sy = 0, sWidth = origWidth, sHeight = origHeight;

    if (cropRatio) {
      // Crop center
      const cropW = Math.round(origWidth * cropRatio);
      const cropH = Math.round(origHeight * cropRatio);
      sx = Math.round((origWidth - cropW) / 2);
      sy = Math.round((origHeight - cropH) / 2);
      sWidth = cropW;
      sHeight = cropH;

      if (cropW > cropH) {
        canvasWidth = Math.min(cropW, maxDim);
        canvasHeight = Math.round((cropH * canvasWidth) / cropW);
      } else {
        canvasHeight = Math.min(cropH, maxDim);
        canvasWidth = Math.round((cropW * canvasHeight) / cropH);
      }
    }

    const canvas = document.createElement("canvas");
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext("2d");

    // Draw image to canvas
    ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, canvasWidth, canvasHeight);

    if (type === "enhanced_crop") {
      try {
        const imgData = ctx.getImageData(0, 0, canvasWidth, canvasHeight);
        const data = imgData.data;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          // Grayscale
          const gray = 0.299 * r + 0.587 * g + 0.114 * b;
          // Increase contrast: scale factor = 2.0
          let newVal = 2.0 * (gray - 128) + 128;
          newVal = Math.max(0, Math.min(255, newVal));
          data[i] = newVal;
          data[i + 1] = newVal;
          data[i + 2] = newVal;
        }
        ctx.putImageData(imgData, 0, 0);
      } catch (e) {
        console.warn("Contrast enhancement failed, using original crop:", e);
      }
    }

    canvas.toBlob(
      (blob) => {
        resolve(blob || img);
      },
      mimeType,
      0.90
    );
  });
};

// Helper to resolve potential UTF-8 decoding issues for non-ASCII characters
const decodeNonAsciiText = (text) => {
  try {
    return decodeURIComponent(escape(text));
  } catch (e) {
    return text;
  }
};

export default function WorkReceipt() {
  const { dictionary } = useContext(LanguageContext);

  // Search states
  const [patientCodeInput, setPatientCodeInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Scanner states
  const [isScanning, setIsScanning] = useState(false);
  const [scanner, setScanner] = useState(null);

  // Loaded job states
  const [jobDetails, setJobDetails] = useState(null);
  const [activeActionsList, setActiveActionsList] = useState([]);
  const [activeAction, setActiveAction] = useState(null);
  const [possibleActions, setPossibleActions] = useState([]);
  const stepFormRef = useRef(null);

  // Transition submission states
  const [selectedStep, setSelectedStep] = useState("");
  const [completionNote, setCompletionNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isStepFormValid, setIsStepFormValid] = useState(true);

  // Photo processing states
  const [isProcessingPhoto, setIsProcessingPhoto] = useState(false);
  const [photoStatus, setPhotoStatus] = useState("");
  const [scannerError, setScannerError] = useState("");

  const handleActiveActionChange = (e) => {
    const actionId = Number(e.target.value);
    const selected = activeActionsList.find((act) => act.Id === actionId);
    if (selected) {
      setIsStepFormValid(true);
      setActiveAction(selected);
    }
  };

  const startScanner = (scannerInstance, qrboxFn) => {
    scannerInstance
      .start(
        { facingMode: "environment" },
        {
          fps: 15,
          qrbox: qrboxFn,
          formatsToSupport: [
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.DATA_MATRIX,
            Html5QrcodeSupportedFormats.AZTEC,
            Html5QrcodeSupportedFormats.PDF_417
          ]
        },
        (decodedText) => {
          const parsedText = decodeNonAsciiText(decodedText);
          setPatientCodeInput(parsedText);
          setIsScanning(false);
          triggerSearch(parsedText);
        },
        () => {}
      )
      .then(() => {
        setScanner(scannerInstance);
      })
      .catch((err) => {
        console.error("Failed to start scanner:", err);
        setError("Failed to access camera. Please check permissions.");
        setIsScanning(false);
      });
  };

  // Handle QR scanner initialization and shutdown
  useEffect(() => {
    let qrScanner = null;
    if (isScanning) {
      // Delay slightly to ensure Modal DOM is fully rendered
      const timer = setTimeout(() => {
        const qrboxFunction = (viewfinderWidth, viewfinderHeight) => {
          const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
          const boxSize = Math.floor(minEdge * 0.7);
          return {
            width: boxSize,
            height: boxSize,
          };
        };

        qrScanner = new Html5Qrcode("qr-reader");
        startScanner(qrScanner, qrboxFunction);
      }, 300);

      return () => {
        clearTimeout(timer);
        if (qrScanner && qrScanner.isScanning) {
          qrScanner
            .stop()
            .catch((err) => console.error("Failed to stop scanner on cleanup:", err));
        }
      };
    }
  }, [isScanning]);

  // Effect to load possible transitions when the selected activeAction changes
  useEffect(() => {
    let active = true;
    async function fetchTransitions() {
      if (!activeAction) {
        setPossibleActions([]);
        setSelectedStep("");
        return;
      }
      try {
        const possibleRes = await WorkService.getPossibleActions(activeAction.Id);
        if (active) {
          setPossibleActions(possibleRes || []);
          if (possibleRes && possibleRes.length > 0) {
            setSelectedStep(possibleRes[0].StepNext);
          } else {
            setSelectedStep("");
          }
        }
      } catch (err) {
        console.error(err);
        if (active) {
          setError("An error occurred while fetching transition steps.");
        }
      }
    }
    fetchTransitions();
    return () => {
      active = false;
    };
  }, [activeAction]);

  const handleCloseScanner = () => {
    setScannerError("");
    setIsProcessingPhoto(false);
    setPhotoStatus("");
    if (scanner && scanner.isScanning) {
      scanner
        .stop()
        .then(() => {
          setIsScanning(false);
          setScanner(null);
        })
        .catch((err) => {
          console.error(err);
          setIsScanning(false);
          setScanner(null);
        });
    } else {
      setIsScanning(false);
    }
  };

  const handlePhotoScan = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsProcessingPhoto(true);
    setPhotoStatus("Loading captured photo...");
    setScannerError("");
    setError("");

    try {
      const img = await loadImage(file);
      const fileScanner = new Html5Qrcode("qr-reader-file-dummy");

      // We will try scanning the photo using different preprocessing pipeline stages
      const stages = [
        { name: "Optimizing full image size...", options: { type: "downscaled", maxDim: 1024 } },
        { name: "Analyzing focused center (60% zoom)...", options: { type: "crop", maxDim: 1024, cropRatio: 0.6 } },
        { name: "Analyzing narrow center (40% zoom)...", options: { type: "crop", maxDim: 1024, cropRatio: 0.4 } },
        { name: "Enhancing contrast and re-scanning...", options: { type: "enhanced_crop", maxDim: 1024, cropRatio: 0.6 } },
      ];

      let decodedText = null;

      for (const stage of stages) {
        setPhotoStatus(stage.name);
        try {
          const processedBlob = await preprocessImage(img, file.type || "image/jpeg", stage.options);
          const processedFile = new File([processedBlob], file.name || "image.jpg", {
            type: file.type || "image/jpeg",
            lastModified: Date.now()
          });

          decodedText = await fileScanner.scanFile(processedFile, false);
          if (decodedText) {
            console.log(`Scan success using stage: ${stage.options.type}`);
            break;
          }
        } catch (err) {
          console.warn(`Stage failed: ${stage.options.type}`, err);
        }
      }

      if (decodedText) {
        const parsedText = decodeNonAsciiText(decodedText);
        setPatientCodeInput(parsedText);
        setIsScanning(false);
        triggerSearch(parsedText);
      } else {
        setScannerError(
          "Could not detect a QR Code in the photo. Please make sure the code is sharp, centered, and not blurry."
        );
      }
    } catch (err) {
      console.error("Error preprocessing image:", err);
      setScannerError("Failed to process photo. Please try manual entry or scan again.");
    } finally {
      setIsProcessingPhoto(false);
      setPhotoStatus("");
      e.target.value = "";
    }
  };

  const triggerSearch = async (code) => {
    setLoading(true);
    setError("");
    setSuccess("");
    setJobDetails(null);
    setActiveActionsList([]);
    setActiveAction(null);
    setPossibleActions([]);
    setSelectedStep("");
    setCompletionNote("");
    setIsStepFormValid(true);

    try {
      const res = await WorkService.getJobFullDetailsByPatientCode(code.trim());
      if (!res || res.length === 0) {
        setError(`No active jobs found for Patient Code: ${code}`);
        setLoading(false);
        return;
      }

      const data = res[0];

      // Map jobDetails
      setJobDetails({
        Id: data.InstanceId,
        PatientId: data.PatientId,
        PatientName: data.PatientFirstName ? `${data.PatientFirstName} ${data.PatientFamilyName || ""}`.trim() : "N/A",
        PatientCode: data.PatientCode || "N/A",
        DoctorName: data.DoctorName || "N/A",
        InstanceType: data.InstanceType,
        DueDate: data.DueDate,
        Note: data.InstanceNote,
      });

      // Map active actions from all rows in results
      const actions = res
        .filter((row) => row.ActionId)
        .map((row) => ({
          Id: row.ActionId,
          ActionStep: row.ActionStep,
          Note: row.ActionNote,
          SystemDate: row.ActionStartDate,
          ActionStepDescription: row.StepDescription || `Step ID: ${row.ActionStep}`,
        }));

      // Deduplicate actions by Id
      const uniqueActions = [];
      const seenIds = new Set();
      for (const act of actions) {
        if (!seenIds.has(act.Id)) {
          seenIds.add(act.Id);
          uniqueActions.push(act);
        }
      }

      setActiveActionsList(uniqueActions);
      if (uniqueActions.length > 0) {
        setActiveAction(uniqueActions[0]);
      } else {
        setActiveAction(null);
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred while fetching details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    triggerSearch(patientCodeInput);
  };

  const handleTransitionSubmit = async (e) => {
    e.preventDefault();
    if (!activeAction) return;
    if (selectedStep === "") {
      setError("Please select a next action/step.");
      return;
    }

    // Validate step-specific form if it exists
    const StepForm = getStepComponent(activeAction.ActionStep, activeAction.ActionStepDescription);
    if (StepForm && stepFormRef.current) {
      const isValid = stepFormRef.current.validate();
      if (!isValid) return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      // Save step-specific details first
      if (StepForm && stepFormRef.current) {
        await stepFormRef.current.onSave();
      }

      await WorkService.completeAction(
        activeAction.Id,
        selectedStep,
        completionNote.trim()
      );
      setSuccess(`Job for patient "${jobDetails.PatientName}" updated successfully to the next stage!`);
      // Reset loaded details
      setJobDetails(null);
      setActiveActionsList([]);
      setActiveAction(null);
      setPossibleActions([]);
      setPatientCodeInput("");
    } catch (err) {
      console.error(err);
      setError("An error occurred while submitting the update. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setPatientCodeInput("");
    setError("");
    setSuccess("");
    setJobDetails(null);
    setActiveActionsList([]);
    setActiveAction(null);
    setPossibleActions([]);
    setSelectedStep("");
    setCompletionNote("");
    setIsStepFormValid(true);
  };

  const StepForm = activeAction
    ? getStepComponent(activeAction.ActionStep, activeAction.ActionStepDescription)
    : null;

  return (
    <div className="work-receipt-wrapper">
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col xs={12} sm={10} md={8} lg={6} xl={5} className="px-3">
            <Card className="shadow-lg main-receipt-card border-0">
              <Card.Header className="text-white text-center py-4 receipt-header-gradient border-0">
                <h2 className="m-0 fw-bold">Orthomagic</h2>
                <p className="m-0 mt-1 opacity-75">
                  {dictionary.WorkReceipt || "Work Status Updater"}
                </p>
              </Card.Header>
              <Card.Body className="p-4">
                {/* Notifications */}
                {error && (
                  <Alert variant="danger" className="border-0 shadow-sm animate-fade-in">
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert variant="success" className="border-0 shadow-sm animate-fade-in">
                    {success}
                  </Alert>
                )}

                {/* Step 1: Input/Scan Patient Code */}
                {!jobDetails && !loading && (
                  <Form onSubmit={handleSearch} className="animate-fade-in">
                    <Form.Group className="mb-4 text-center">
                      <Form.Label className="fs-5 fw-semibold text-secondary mb-3">
                        Enter or Scan Patient Code to Begin
                      </Form.Label>
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Control
                          type="text"
                          size="lg"
                          className="text-center rounded-pill job-id-input shadow-sm flex-grow-1"
                          placeholder="e.g. 04864EE"
                          value={patientCodeInput}
                          onChange={(e) => setPatientCodeInput(e.target.value)}
                        />
                        <Button
                          variant="outline-primary"
                          size="lg"
                          className="rounded-circle px-3 scan-qr-trigger-btn shadow-sm d-flex align-items-center justify-content-center"
                          onClick={() => setIsScanning(true)}
                          type="button"
                          title="Scan QR Code"
                          style={{ width: "52px", height: "52px", flexShrink: 0 }}
                        >
                          <i className="fa fa-qrcode fs-4"></i>
                        </Button>
                      </div>
                    </Form.Group>
                    <Button
                      variant="primary"
                      size="lg"
                      type="submit"
                      className="w-100 rounded-pill py-3 fw-bold search-button shadow"
                    >
                      Find Patient Status
                    </Button>
                  </Form>
                )}

                {/* Loading spinner */}
                {loading && (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" size="lg" className="mb-3" />
                    <p className="text-muted">Fetching job details from database...</p>
                  </div>
                )}

                {/* Step 2: Job Details & Transitions */}
                {jobDetails && (
                  <div className="animate-fade-in">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h4 className="fw-bold m-0 text-dark">Job Details</h4>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => triggerSearch(jobDetails.PatientCode)}
                          className="rounded-pill px-3"
                          disabled={loading}
                        >
                          <i className="fa fa-sync-alt me-1"></i> Reload
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={resetForm}
                          className="rounded-pill px-3"
                        >
                          Search New
                        </Button>
                      </div>
                    </div>

                    {/* Instance Details */}
                    <Card className="bg-light border-0 rounded-4 p-3 mb-4 shadow-sm">
                      <Stack gap={2}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Job ID:</span>
                          <span className="fw-bold">{jobDetails.Id}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Patient Name:</span>
                          <span className="fw-bold">{jobDetails.PatientName}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Patient Code:</span>
                          <span className="fw-bold">{jobDetails.PatientCode}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Doctor Name:</span>
                          <span className="fw-bold">{jobDetails.DoctorName}</span>
                        </div>

                        {jobDetails.DueDate && (
                          <div className="d-flex justify-content-between">
                            <span className="text-muted small">Due Date:</span>
                            <span className="fw-semibold text-danger">
                              {new Date(jobDetails.DueDate).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        <hr className="my-2 opacity-50" />
                        <div>
                          <span className="text-muted small d-block mb-1">Job Description:</span>
                          <p className="small mb-0 text-secondary bg-white p-2 rounded border border-light">
                            {jobDetails.Note || "No description provided."}
                          </p>
                        </div>
                      </Stack>
                    </Card>

                    {/* Active Action Section */}
                    {activeActionsList.length > 0 ? (
                      <Form onSubmit={handleTransitionSubmit}>
                        {activeActionsList.length > 1 && (
                          <div className="mb-4 p-3 bg-light rounded-4 border border-light shadow-sm">
                            <Form.Group>
                              <Form.Label className="fw-bold text-secondary mb-2">
                                {dictionary.MultipleStepsFound || "Multiple steps are in progress. Please select your current step:"}
                              </Form.Label>
                              <Form.Select
                                className="rounded-pill px-3 py-2 border-primary shadow-sm fw-semibold"
                                value={activeAction?.Id || ""}
                                onChange={handleActiveActionChange}
                              >
                                {activeActionsList.map((act) => (
                                  <option key={act.Id} value={act.Id}>
                                    {act.ActionStepDescription} (Started: {new Date(act.SystemDate).toLocaleDateString()})
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </div>
                        )}

                        <h4 className="fw-bold mb-3 text-dark">
                          {dictionary.SelectCurrentStep || "Current Stage"}
                        </h4>

                        {activeAction && (
                          <Card className="active-action-card border-0 rounded-4 p-3 mb-4 shadow-sm animate-fade-in">
                            <Stack gap={1}>
                              <div className="d-flex justify-content-between align-items-center">
                                <Badge
                                  bg="warning"
                                  text="dark"
                                  className="fs-6 py-2 px-3 rounded-pill fw-bold"
                                >
                                  {activeAction.ActionStepDescription ||
                                    `Step ID: ${activeAction.ActionStep}`}
                                </Badge>
                                <span className="text-muted small">
                                  Started: {new Date(activeAction.SystemDate).toLocaleDateString()}
                                </span>
                              </div>
                              {activeAction.Note && (
                                <div className="mt-2 bg-white p-2 rounded border border-light">
                                  <span className="text-muted small d-block">Stage Note:</span>
                                  <span className="small text-secondary">
                                    {activeAction.Note}
                                  </span>
                                </div>
                              )}
                            </Stack>
                          </Card>
                        )}

                        {/* Dynamic Step-Specific Fields */}
                        {StepForm && (
                          <StepForm
                            ref={stepFormRef}
                            patientId={jobDetails.PatientId}
                            instanceId={jobDetails.Id}
                            actionId={activeAction.Id}
                            onValidityChange={setIsStepFormValid}
                          />
                        )}

                        {/* Next Actions Selection */}
                        <h4 className="fw-bold mb-3 text-dark">Action Transition</h4>
                        {possibleActions.length > 0 ? (
                          <>
                            <Form.Group className="mb-4">
                              <Form.Label className="fw-semibold text-secondary mb-2">
                                Choose Next Step
                              </Form.Label>
                              <div className="possible-steps-grid gap-2 d-flex flex-wrap">
                                {possibleActions.map((act) => (
                                  <Button
                                    key={act.StepNext}
                                    variant={
                                      selectedStep === act.StepNext
                                        ? "primary"
                                        : "outline-primary"
                                    }
                                    className="flex-grow-1 rounded-pill py-2 px-3 text-center transition-btn"
                                    onClick={() => setSelectedStep(act.StepNext)}
                                    type="button"
                                  >
                                    {act.StepNext === 0
                                      ? "Sadece Notu Güncelle"
                                      : act.DscNext || `Move to Step ${act.StepNext}`}
                                  </Button>
                                ))}
                              </div>
                            </Form.Group>

                            <Form.Group className="mb-4">
                              <Form.Label className="fw-semibold text-secondary mb-2">
                                Transition Note
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                className="rounded-3 shadow-sm border-light"
                                placeholder="Describe actions taken or notes for the next stage..."
                                value={completionNote}
                                onChange={(e) => setCompletionNote(e.target.value)}
                              />
                            </Form.Group>

                            <Button
                              variant="success"
                              size="lg"
                              type="submit"
                              className="w-100 rounded-pill py-3 fw-bold submit-button shadow"
                              disabled={submitting || !isStepFormValid}
                            >
                              {submitting ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  Updating Stage...
                                </>
                              ) : (
                                "Complete Step"
                              )}
                            </Button>
                          </>
                        ) : (
                          <Alert variant="info" className="border-0 shadow-sm rounded-4">
                            No possible next steps are registered/available for this user type.
                          </Alert>
                        )}
                      </Form>
                    ) : (
                      <Alert
                        variant="success"
                        className="border-0 shadow-sm rounded-4 text-center py-4"
                      >
                        <h5 className="fw-bold text-success mb-2">Workflow Completed</h5>
                        This job is fully completed. No active/pending action steps remain.
                      </Alert>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* QR Scanner Viewfinder Modal */}
      <Modal show={isScanning} onHide={handleCloseScanner} centered size="md" className="work-scanner-modal">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-dark">Scan Work Card</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 text-center">
          <div className="scanner-viewfinder-container mb-3 position-relative">
            <div id="qr-reader"></div>
            <div className="scanner-overlay-laser"></div>
          </div>

          <p className="text-secondary small m-0">Position the QR code inside the viewfinder window</p>

          {/* Fallback Photo Capture for High-Res Autofocus */}
          <div className="my-3 border-top pt-3">
            <span className="text-secondary small d-block mb-2 fw-semibold">
              Focus issues? Try capturing a high-res photo instead:
            </span>
            <Button
              variant="success"
              className="rounded-pill px-4 py-2 fw-bold d-inline-flex align-items-center gap-2 shadow-sm animate-pulse"
              onClick={() => document.getElementById("hidden-camera-input").click()}
              disabled={isProcessingPhoto}
            >
              <i className="fa fa-camera"></i>
              {isProcessingPhoto ? "Processing Photo..." : "Take Zoomed Photo"}
            </Button>
            <input
              type="file"
              id="hidden-camera-input"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoScan}
              style={{ display: "none" }}
            />
          </div>

          {/* Loader and error messages inside the modal */}
          {isProcessingPhoto && (
            <div className="my-3 text-center animate-fade-in">
              <Spinner animation="border" variant="success" size="sm" className="me-2" />
              <span className="text-secondary small fw-semibold">{photoStatus || "Analyzing high-res photo..."}</span>
            </div>
          )}

          {scannerError && (
            <Alert variant="danger" className="border-0 shadow-sm rounded-4 py-2 px-3 my-3 small animate-fade-in" onClose={() => setScannerError("")} dismissible>
              <i className="fa fa-exclamation-triangle me-2"></i>
              {scannerError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 justify-content-center">
          <Button variant="outline-secondary" className="rounded-pill px-4" onClick={handleCloseScanner}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Hidden dummy element for file scanner initialization */}
      <div id="qr-reader-file-dummy" style={{ display: "none" }}></div>
    </div>
  );
}
